import { useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useModuleAccess } from '@/permissions';
import { useGetAdvisorGroupPageVisibilityQuery } from '@/store/pageVisibilityApi';
import { AdvisorGroupSetting } from '@/store/settingsApi';
import { WARootState } from '@/store/store';
import { AdvisorInvestor } from '@/store/usersApi';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';
import { normalizePath } from '@/utils/appMenuFilter';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';

export const useTabVisibility = (
  client: AdvisorInvestor,
  advisorGroupSettings: AdvisorGroupSetting[],
  areAdvisorGroupSettingsResolved: boolean,
) => {
  const { validateByModule } = useModuleAccess();

  // Setup -> Pages lives at the advisor-group (firm) level: the logged-in
  // advisor's own advisorGroupId, not something resolved per client.
  const userInfo = useSelector((s: WARootState) => s.appView.UserInfo);
  const advisorGroupId = deriveAdvisorGroupId(userInfo);

  const {
    currentData: pageVisibility,
    isError: isPageVisibilityError,
    isUninitialized: isPageVisibilityUninitialized,
  } = useGetAdvisorGroupPageVisibilityQuery(advisorGroupId ?? skipToken);

  // Fail-closed: while loading, on error, or if advisorGroupId is
  // unresolved, pageVisibility is undefined and every path resolves to false.
  const clientCanSee = (path: string): boolean =>
    pageVisibility?.[normalizePath(path)] === true;

  // Reached a final state (success/error), or never dispatched at all
  // (isUninitialized, when advisorGroupId is unresolvable) — distinct from
  // "still loading", which callers must not treat as resolved.
  const isPageVisibilityResolved =
    pageVisibility !== undefined ||
    isPageVisibilityError ||
    isPageVisibilityUninitialized;

  // Both async sources that feed the isXEnabled flags below (pageVisibility
  // and advisorGroupSettings) must reach a final state before a caller can
  // safely pick or redirect away from a tab. Combined here, not in
  // ClientContent, so the routed page (a future consumer of this hook) gets
  // the same guarantee without recombining the two gates itself.
  const areTabFlagsResolved =
    isPageVisibilityResolved && areAdvisorGroupSettingsResolved;

  // Also logged when advisorGroupId can't be resolved: the query is skipped
  // (skipToken) and never becomes isError, so without this branch several
  // tabs silently disappear with no telemetry.
  useEffect(() => {
    if (isPageVisibilityError) {
      datadogRum.addError(new Error('page_config_fetch_failed'), {
        advisorGroupId,
      });
      return;
    }
    if (advisorGroupId === null && isPageVisibilityUninitialized) {
      datadogRum.addError(new Error('page_config_advisor_group_unresolved'), {
        advisorGroupId,
      });
    }
  }, [isPageVisibilityError, advisorGroupId, isPageVisibilityUninitialized]);

  const isBalanceSheetEnabled =
    useCanNavigateTo('/advisor/clients-wealth/balance-sheet') &&
    clientCanSee('/BalanceSheet');
  // Legacy Behavior: Disable this tab if the investor is part of a household.
  const isDocumentsEnabled =
    useCanNavigateTo('/advisor/documents/document-vault') &&
    !client.InHousehold &&
    clientCanSee('/Tools/Documents2');
  const isAccountsEnabled =
    useCanNavigateTo('/advisor/clients-wealth/accounts') &&
    clientCanSee('/Portfolio/Accounts');
  const isHoldingsEnabled =
    useCanNavigateTo('/advisor/clients-wealth/holdings') &&
    clientCanSee('/Portfolio/Holdings');
  const isDiversificationEnabled =
    useCanNavigateTo('/advisor/clients-wealth/asset-allocation') &&
    clientCanSee('/Portfolio/Diversification');
  const isCashFlowEnabled =
    useCanNavigateTo('/advisor/clients-wealth/cash-flow') &&
    clientCanSee('/CashFlow');
  const isInvestmentActivityEnabled =
    !AdvisorGroupSettings(advisorGroupSettings).getByKey(
      'ShowAllTransactionsInCashFlow',
    ) && clientCanSee('/Portfolio/Trading');

  // Legacy Behavior: Disable this tab if the investor is part of a household.
  // No Setup -> Pages equivalent is known for Portal Activity; do not gate it
  // on clientCanSee.
  const isPortalActivityEnabled = !client.InHousehold;
  // TODO: Page out scope / hasn't been built.
  const isToDoEnabled = false;

  const isInsightsEnabled = validateByModule('AdvisorInsights');
  // Gains & Losses combines the module flag (Condición 1) AND page visibility
  // (Condición 2) -- unlike Insights, which has no Setup -> Pages equivalent.
  const isGainsLossesEnabled =
    validateByModule('Gains&Losses') &&
    clientCanSee('/Portfolio/GainsAndLosses');

  return {
    isBalanceSheetEnabled,
    isDocumentsEnabled,
    isAccountsEnabled,
    isHoldingsEnabled,
    isDiversificationEnabled,
    isInsightsEnabled,
    isGainsLossesEnabled,
    isCashFlowEnabled,
    isPortalActivityEnabled,
    isToDoEnabled,
    isInvestmentActivityEnabled,
    areTabFlagsResolved,
  };
};
